import { selectTenant, updateTenantBalance } from '@saaga/shared/store/slices/billing.slice'
import { useAppDispatch, useAppSelector } from '@saaga/shared/store/store'
import { trackEvent } from '@saaga/shared/utils/analytics.util'
import { microsToDollars } from '@saaga/shared/utils/billing.util'
import { logger } from '@saaga/shared/utils/logger.util'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'

import { useBillingClient } from './use-billing-client.hook'

export interface UseRedeemCodeReturn {
  redeemCode: (code: string) => Promise<boolean>
  isLoading: boolean
  error: string | null
}

export const useRedeemCode = (): UseRedeemCodeReturn => {
  const dispatch = useAppDispatch()
  const tenant = useAppSelector(selectTenant)
  const { redeemCode: redeemCodeApi } = useBillingClient()

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const redeemCode = useCallback(
    async (code: string): Promise<boolean> => {
      setIsLoading(true)
      setError(null)

      try {
        const response = await redeemCodeApi(code)

        if (!response.success) {
          setError(response.message)
          toast.error(response.message)
          return false
        }

        const creditedAmount = microsToDollars(response.creditAmount)
        toast.success(`Successfully added $${creditedAmount.toFixed(2)} to your balance`)
        trackEvent('code_redeemed', {})

        const currentBalance = BigInt(tenant?.balance ?? '0')
        const creditAmount = BigInt(response.creditAmount)
        const newBalance = (currentBalance + creditAmount).toString()
        dispatch(updateTenantBalance(newBalance))

        return true
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to redeem code'
        setError(errorMessage)
        toast.error(errorMessage)
        logger.error('Failed to redeem code', { error: err, code })
        return false
      } finally {
        setIsLoading(false)
      }
    },
    [redeemCodeApi, tenant?.balance, dispatch]
  )

  return {
    redeemCode,
    isLoading,
    error,
  }
}
